import React from 'react'
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Container,
  Link,
  Fade,
  Slide,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate()

  const sectionStyles = {
    mb: 4,
  }

  const headingStyles = {
    fontWeight: 600,
    color: 'grey.800',
    mb: 2,
    fontSize: { xs: '1.125rem', sm: '1.25rem' },
  }

  const subHeadingStyles = {
    fontWeight: 600,
    color: 'grey.700',
    mb: 1.5,
    fontSize: { xs: '1rem', sm: '1.0625rem' },
  }

  const bodyStyles = {
    color: 'grey.600',
    lineHeight: 1.7,
    mb: 2,
    fontSize: { xs: '0.9375rem', sm: '1rem' },
  }

  const listStyles = {
    color: 'grey.600',
    lineHeight: 1.8,
    pl: 2.5,
    mb: 2,
    fontSize: { xs: '0.9375rem', sm: '1rem' },
    listStyleType: 'disc',
    '& li': {
      mb: 0.5,
    },
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Fade in timeout={800}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            height: '80px',
            flexShrink: 0,
          }}
        >
          <Toolbar
            sx={{ justifyContent: 'space-between', py: 1, height: '100%' }}
          >
            <Slide direction="right" in timeout={1000}>
              <Box
                component="img"
                src="/logo.svg"
                alt="PillarThera Logo"
                sx={{
                  height: { xs: 32, sm: 60 },
                  width: 'auto',
                  cursor: 'pointer',
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => navigate('/')}
              />
            </Slide>
          </Toolbar>
        </AppBar>
      </Fade>

      {/* Main Content */}
      <Box sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Fade in timeout={600}>
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                p: { xs: 3, sm: 4, md: 5 },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'grey.900',
                  mb: 0.5,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                }}
              >
                PillarThera Privacy Policy
              </Typography>
              <Typography
                sx={{
                  color: 'grey.500',
                  mb: 4,
                  fontSize: '0.875rem',
                }}
              >
                Effective Date: February 21, 2026
              </Typography>

              <Typography sx={bodyStyles}>
                At PillarThera, your privacy and the protection of your personal
                and health-related information are extremely important to us. We
                are committed to transparency about how we collect, use, store,
                and share your information.
              </Typography>
              <Typography sx={bodyStyles}>
                This Privacy Policy explains how PillarThera (&quot;we,&quot;
                &quot;our,&quot; or &quot;us&quot;) collects, uses, and
                safeguards your information when you use our mobile
                applications, website, patient portal, and provider platform
                (collectively, the &quot;Services&quot;).
              </Typography>
              <Typography sx={bodyStyles}>
                PillarThera is a healthcare coordination and therapy management
                platform connecting patients with licensed therapy providers
                offering Speech Therapy, Occupational Therapy, and Physical
                Therapy.
              </Typography>

              {/* Section 1 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>
                  1. Information We Collect
                </Typography>
                <Typography sx={subHeadingStyles}>
                  a. Information You Provide
                </Typography>
                <Typography sx={bodyStyles}>
                  When you register or use our Services, we may collect:
                </Typography>
                <Typography sx={subHeadingStyles}>
                  Personal Information
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Date of birth</li>
                  <li>Address</li>
                  <li>Emergency contact information</li>
                  <li>Profile photo (optional)</li>
                </Box>
                <Typography sx={subHeadingStyles}>
                  Patient Health & Therapy Information
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Therapy needs and goals</li>
                  <li>Medical history relevant to therapy services</li>
                  <li>Referrals or prescriptions (if uploaded)</li>
                  <li>
                    Insurance information (member ID, payer, policy holder
                    details)
                  </li>
                  <li>Intake forms and consent forms</li>
                </Box>
                <Typography sx={subHeadingStyles}>
                  Payment Information
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Self-pay billing details</li>
                  <li>
                    Card information processed securely via Stripe (we do not
                    store card numbers)
                  </li>
                </Box>
                <Typography sx={subHeadingStyles}>
                  Provider Information (Therapists)
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>License details</li>
                  <li>Board certification</li>
                  <li>NPI number</li>
                  <li>Professional credentials</li>
                  <li>Practice location(s)</li>
                  <li>Verification information</li>
                </Box>
                <Typography sx={subHeadingStyles}>Support Requests</Typography>
                <Typography sx={bodyStyles}>
                  Information you provide when contacting support at{' '}
                  <Link
                    href="mailto:support@PillarThera.com"
                    sx={{ color: '#2E7DAF', fontWeight: 500 }}
                  >
                    support@PillarThera.com
                  </Link>
                </Typography>
                <Typography sx={subHeadingStyles}>
                  b. Automatically Collected Information
                </Typography>
                <Typography sx={bodyStyles}>
                  When you use PillarThera, we automatically collect certain
                  technical data:
                </Typography>
                <Typography sx={subHeadingStyles}>
                  Device & Usage Data
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>IP address</li>
                  <li>Device type</li>
                  <li>Operating system</li>
                  <li>Browser type</li>
                  <li>App version</li>
                  <li>Session activity</li>
                </Box>
                <Typography sx={subHeadingStyles}>Analytics Data</Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Pages or screens viewed</li>
                  <li>Features used</li>
                  <li>Session duration</li>
                  <li>Error logs</li>
                </Box>
                <Typography sx={bodyStyles}>
                  We may use tools such as Firebase Analytics, crash reporting,
                  and performance monitoring to improve reliability.
                </Typography>
                <Typography sx={subHeadingStyles}>Identifiers</Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Mobile device identifiers (IDFA/GAID where permitted)</li>
                  <li>Apple App Tracking Transparency (ATT)</li>
                </Box>
                <Typography sx={bodyStyles}>
                  On iOS devices, we request tracking permission in compliance
                  with Apple&apos;s ATT framework. Users can deny tracking
                  without affecting core app functionality.
                </Typography>
              </Box>

              {/* Section 2 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>
                  2. How We Use Your Information
                </Typography>
                <Typography sx={bodyStyles}>
                  We use your information to:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Create and manage your PillarThera account</li>
                  <li>Connect patients with therapy providers</li>
                  <li>Schedule therapy sessions</li>
                  <li>Enable telehealth and in-person appointments</li>
                  <li>Process payments (self-pay sessions)</li>
                  <li>Submit insurance claims to payers</li>
                  <li>Verify provider credentials and licensing</li>
                  <li>Provide customer support</li>
                  <li>Send appointment reminders and billing notifications</li>
                  <li>Improve app performance and features</li>
                  <li>Detect fraud, abuse, and policy violations</li>
                  <li>
                    Comply with legal and healthcare documentation requirements
                  </li>
                </Box>
                <Typography sx={bodyStyles}>
                  We do not sell your personal information to advertisers.
                </Typography>
              </Box>

              {/* Section 3 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>
                  3. How We Share Information
                </Typography>
                <Typography sx={bodyStyles}>
                  We only share information when necessary to operate the
                  platform:
                </Typography>
                <Typography sx={subHeadingStyles}>
                  Healthcare Providers
                </Typography>
                <Typography sx={bodyStyles}>
                  Patient information is shared with the selected therapist for:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Treatment</li>
                  <li>Scheduling</li>
                  <li>Documentation</li>
                  <li>Care delivery</li>
                </Box>
                <Typography sx={subHeadingStyles}>
                  Insurance & Billing Partners
                </Typography>
                <Typography sx={bodyStyles}>
                  We may share relevant information with:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Insurance clearinghouses</li>
                  <li>Payers</li>
                  <li>Claim processing partners</li>
                </Box>
                <Typography sx={bodyStyles}>
                  for submitting and tracking therapy claims.
                </Typography>
                <Typography sx={subHeadingStyles}>Service Providers</Typography>
                <Typography sx={bodyStyles}>
                  We use trusted vendors, including:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Stripe (payments)</li>
                  <li>Secure cloud hosting providers</li>
                  <li>Analytics and performance monitoring services</li>
                  <li>Notification and messaging services</li>
                </Box>
                <Typography sx={bodyStyles}>
                  These providers may only use data to perform services on our
                  behalf.
                </Typography>
                <Typography sx={subHeadingStyles}>
                  Legal Requirements
                </Typography>
                <Typography sx={bodyStyles}>
                  We may disclose information:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>To comply with applicable law</li>
                  <li>To respond to legal requests</li>
                  <li>To protect users, providers, or the platform</li>
                </Box>
                <Typography sx={bodyStyles}>
                  We do not share data with third-party marketing companies.
                </Typography>
              </Box>

              {/* Section 4 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>
                  4. Cookies & Tracking Technologies
                </Typography>
                <Typography sx={bodyStyles}>
                  We use cookies and similar technologies to:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Maintain login sessions</li>
                  <li>Remember preferences</li>
                  <li>Enable secure functionality</li>
                  <li>Analyze usage patterns</li>
                </Box>
                <Typography sx={bodyStyles}>
                  Tracking is anonymous unless you provide consent. You can
                  manage cookies through your browser or device settings.
                </Typography>
              </Box>

              {/* Section 5 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>5. Data Retention</Typography>
                <Typography sx={bodyStyles}>
                  We retain personal and therapy records:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>While your account is active</li>
                  <li>
                    As required for legal, billing, and healthcare documentation
                    purposes
                  </li>
                  <li>
                    Until you request deletion (subject to legal obligations
                    such as medical record retention laws)
                  </li>
                  <li>
                    Insurance and clinical documentation may be retained for
                    required healthcare compliance periods
                  </li>
                </Box>
              </Box>

              {/* Section 6 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>
                  6. Your Privacy Choices
                </Typography>
                <Typography sx={bodyStyles}>You may:</Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Request access to your data</li>
                  <li>Update or correct information</li>
                  <li>Opt-out of marketing communications</li>
                </Box>
                <Typography sx={bodyStyles}>
                  To make a request, contact:{' '}
                  <Link
                    href="mailto:support@PillarThera.com"
                    sx={{ color: '#2E7DAF', fontWeight: 500 }}
                  >
                    support@PillarThera.com
                  </Link>
                  . We will respond within a reasonable timeframe as required by
                  applicable privacy laws.
                </Typography>
              </Box>

              {/* Section 7 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>
                  7. Children&apos;s Privacy
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>
                    PillarThera may be used for pediatric therapy services;
                    however, accounts for minors must be created and managed by
                    a parent or legal guardian.
                  </li>
                  <li>
                    We do not knowingly collect personal information from
                    children without parental consent.
                  </li>
                  <li>
                    If you believe a child submitted information without
                    permission, contact us immediately.
                  </li>
                </Box>
              </Box>

              {/* Section 8 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>8. Data Security</Typography>
                <Typography sx={bodyStyles}>
                  We implement administrative, technical, and physical
                  safeguards including:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>HTTPS encryption</li>
                  <li>Secure cloud infrastructure</li>
                  <li>Role-based access control</li>
                  <li>Provider verification</li>
                  <li>Continuous monitoring and vulnerability testing</li>
                </Box>
                <Typography sx={bodyStyles}>
                  While no system is 100% secure, we follow industry best
                  practices to protect your information.
                </Typography>
              </Box>

              {/* Section 9 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>
                  9. International Users
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>PillarThera operates primarily in the United States.</li>
                  <li>
                    By using our Services outside the U.S., you consent to your
                    information being processed and stored in the United States.
                  </li>
                </Box>
              </Box>

              {/* Section 10 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>
                  10. Changes to This Policy
                </Typography>
                <Typography sx={bodyStyles}>
                  We may update this Privacy Policy from time to time.
                </Typography>
                <Typography sx={bodyStyles}>
                  If significant changes occur, we will notify users via:
                </Typography>
                <Box component="ul" sx={listStyles}>
                  <li>Email</li>
                  <li>In-app notification</li>
                  <li>Website notice</li>
                </Box>
                <Typography sx={bodyStyles}>
                  Continued use of the Services after changes means you accept
                  the updated policy.
                </Typography>
              </Box>

              {/* Section 11 */}
              <Box sx={sectionStyles}>
                <Typography sx={headingStyles}>11. Contact Us</Typography>
                <Typography sx={bodyStyles}>PillarThera Support</Typography>
                <Box component="ul" sx={listStyles}>
                  <li>
                    Email:{' '}
                    <Link
                      href="mailto:support@PillarThera.com"
                      sx={{ color: '#2E7DAF', fontWeight: 500 }}
                    >
                      support@PillarThera.com
                    </Link>
                  </li>
                  <li>
                    Website:{' '}
                    <Link
                      href="https://app.pillarthera.com/support"
                      sx={{ color: '#2E7DAF', fontWeight: 500 }}
                    >
                      https://app.pillarthera.com/support
                    </Link>
                  </li>
                </Box>
              </Box>

              {/* Legal Disclaimer */}
              <Box
                sx={{
                  mt: 5,
                  pt: 4,
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Typography sx={subHeadingStyles}>Legal Disclaimer</Typography>
                <Box component="ul" sx={listStyles}>
                  <li>
                    PillarThera is not a healthcare provider, medical practice,
                    or insurance company.
                  </li>
                  <li>
                    PillarThera is a technology platform that facilitates
                    scheduling, billing, documentation, and communication
                    between patients and licensed therapy providers.
                  </li>
                  <li>
                    All clinical services, diagnoses, and treatments are
                    provided solely by independent licensed therapists.
                  </li>
                </Box>
              </Box>

              {/* Back link */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Link
                  component="button"
                  onClick={() => navigate('/')}
                  sx={{
                    color: '#2E7DAF',
                    fontWeight: 500,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Back to Home
                </Link>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  )
}

export default PrivacyPage
